// ========================================
// MODÈLES POUR GESTION DES DÉPENSES
// Gestion des dépenses d'entreprise (y compris douane et transport)
// ========================================

import { Payment, TRANSACTION_TYPES, PAYMENT_METHODS } from './paymentModels';
import isoStandardIdGenerator from '../utils/ISOStandardIdGenerator';

// Catégories de dépenses
export const EXPENSE_CATEGORIES = {
  RENT: 'rent',
  UTILITIES: 'utilities',
  SALARIES: 'salaries',
  MARKETING: 'marketing',
  MAINTENANCE: 'maintenance',
  OFFICE: 'office',
  TRAVEL: 'travel',
  TAXES: 'taxes',
  INTEREST: 'interest',
  CUSTOMS: 'customs',
  TRANSPORT: 'transport',
  SHIPPING: 'shipping',
  OTHER: 'other'
};

// Modèle de dépense
export class Expense {
  constructor(data = {}) {
    this.id = data.id || this.generateId();
    this.date = data.date || new Date().toISOString();
    this.category = data.category || EXPENSE_CATEGORIES.OTHER;
    this.description = data.description || '';
    this.amount = data.amount || 0;
    this.paymentMethod = data.paymentMethod || PAYMENT_METHODS.CASH;
    this.supplierId = data.supplierId || null;
    this.receipt = data.receipt || null;
    this.businessId = data.businessId || null;
    this.workerId = data.workerId || null;
    this.workerName = data.workerName || null;
    this.actorRole = data.actorRole || null;
    this.fundingSource = data.fundingSource || 'register';
    this.registerPortion = data.registerPortion ?? null;
    this.externalPortion = data.externalPortion ?? null;
    this.setupPhasePayment = data.setupPhasePayment || false;
    this.notes = data.notes || '';
    this.payment = data.payment || null;
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();

    this.orderId = data.orderId || null;
    this.purchaseId = data.purchaseId || null;
    this.isCustomsDuty = data.isCustomsDuty || this.category === EXPENSE_CATEGORIES.CUSTOMS;
    this.isTransportCost =
      data.isTransportCost ||
      this.category === EXPENSE_CATEGORIES.TRANSPORT ||
      this.category === EXPENSE_CATEGORIES.SHIPPING;
  }

  generateId() {
    return isoStandardIdGenerator.generateDepenseId();
  }

  createPayment() {
    if (this.payment) {
      return this.payment;
    }

    this.payment = new Payment({
      amount: this.amount,
      paymentMethod: this.paymentMethod,
      transactionType: TRANSACTION_TYPES.EXPENSE_PAYMENT,
      referenceId: this.id,
      supplierId: this.supplierId,
      paymentDate: this.date,
      notes: `${this.description || 'Expense'} - ${this.category}`,
      workerId: this.workerId
    });

    return this.payment;
  }

  isCustoms() {
    return this.category === EXPENSE_CATEGORIES.CUSTOMS || this.isCustomsDuty;
  }

  isTransport() {
    return (
      this.category === EXPENSE_CATEGORIES.TRANSPORT ||
      this.category === EXPENSE_CATEGORIES.SHIPPING ||
      this.isTransportCost
    );
  }

  isOrderRelated() {
    return !!this.orderId;
  }

  isPurchaseRelated() {
    return !!this.purchaseId;
  }
}

export class ExpenseUtils {
  static filterByCategory(expenses, category) {
    return expenses.filter((exp) => exp.category === category);
  }

  static filterByOrder(expenses, orderId) {
    return expenses.filter((exp) => exp.orderId === orderId);
  }

  static filterByPurchase(expenses, purchaseId) {
    return expenses.filter((exp) => exp.purchaseId === purchaseId);
  }

  static calculateTotal(expenses) {
    return expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
  }

  static calculateTotalByCategory(expenses) {
    const totals = {};
    expenses.forEach((exp) => {
      const category = exp.category || EXPENSE_CATEGORIES.OTHER;
      totals[category] = (totals[category] || 0) + (exp.amount || 0);
    });
    return totals;
  }
}
